import { Clock, Tag } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { RECIPES } from "@/data/recipes";
import { SEO } from "@/components/Seo";

export default function Recipes() {
  return (
    <div data-testid="recipes-page">
      <SEO
        title="Recipes & Rituals — Clean Indian Food Journal | Pranivaa Kitchens"
        description="Clean vegetarian and vegan Indian recipes tested on real weeknights — kanji, methi moong chilla, beetroot thoran and more, each tagged by health condition."
        path="/recipes"
      />
      <section className="pt-28 md:pt-40 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-clay font-semibold mb-5">
              Recipes & Rituals
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.06] text-ink font-semibold max-w-3xl mb-6">
              The journal: clean Indian food, tested on real weeknights.
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-ink-soft max-w-2xl">
              Every recipe carries its condition tag and prep time. No seventeen-ingredient projects —
              food you will actually make on a Tuesday.
            </p>
          </Reveal>
        </div>
      </section>
      <section className="pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {RECIPES.map((r, i) => (
            <Reveal key={r.id}>
              <article
                data-testid={r.testId}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-center ${i % 2 === 1 ? "" : ""}`}
              >
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="overflow-hidden rounded-3xl shadow-lg group">
                    <img loading="lazy" decoding="async"
                      src={r.image}
                      alt={r.imageAlt}
                      className="w-full h-72 md:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1 lg:pr-6" : "lg:pl-6"}`}>
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.12em] uppercase font-semibold text-clay">
                      <Tag size={12} /> {r.tag}
                    </span>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-ink-muted">
                      <Clock size={12} /> {r.time}
                    </span>
                  </div>
                  <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-ink tracking-tight mb-3">
                    {r.title}
                  </h2>
                  <p className="text-base leading-relaxed text-ink-soft mb-6">{r.excerpt}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-paper-card border border-[#E7E2D7] rounded-2xl p-5">
                      <p className="font-mono text-[11px] tracking-[0.15em] uppercase font-semibold text-leaf mb-3">
                        Ingredients
                      </p>
                      <ul className="space-y-2 text-sm text-ink-soft">
                        {r.ingredients.map((ing, j) => (
                          <li key={j} className="flex gap-2"><span className="text-turmeric-deep">•</span>{ing}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-paper-card border border-[#E7E2D7] rounded-2xl p-5">
                      <p className="font-mono text-[11px] tracking-[0.15em] uppercase font-semibold text-beet mb-3">
                        Method
                      </p>
                      <ol className="space-y-2 text-sm text-ink-soft list-none">
                        {r.steps.map((s, j) => (
                          <li key={j} className="flex gap-2.5">
                            <span className="font-mono text-clay font-semibold shrink-0">{j + 1}.</span>{s}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
